/**
 * Every React Query cache that holds order-shaped data.
 *
 * Order surfaces read the same order through a dozen differently-named caches
 * (`order-detail`, `order-children-legs`, the edit dialog's own
 * `order-child-legs` / `order-single-leg-advanced` / `order-edit-leg-count`,
 * the per-leg trips, the counts...). Any write path that only invalidates a
 * subset leaves the rest showing pre-write values until a hard refresh — which
 * is exactly how the edit form kept rendering stale fields after a save or a
 * leg status update.
 *
 * Keys are intentionally listed as PREFIXES (no order id): React Query does a
 * partial-array match, so a prefix hits every id/tenant variant, including the
 * leg-id-keyed ones. One extra refetch is cheaper than a stale form.
 */
export const ORDER_SCOPED_QUERY_KEYS = [
  ["order"],
  ["order-detail"],
  ["order-children"],
  ["order-children-legs"],
  ["order-child-legs"],
  ["order-single-leg-advanced"],
  ["order-edit-leg-count"],
  ["active-leg-trips"],
  ["order-leg-summaries"],
  ["order-leg-asset-summaries"],
  ["order-unique-id"],
  ["order-change-log"],
  ["order-change-events"],
  ["order-cancelled-by"],
  ["order-load-status-at-delivery"],
  ["pending-bol-eligibility"],
  ["eapi-trip-verify"],
  ["trailer-load-status"],
] as const;

/** List/board caches that show orders in aggregate. */
export const ORDER_LIST_QUERY_KEYS = [
  ["orders"],
  ["schedule-orders"],
  ["tractor-conflict-ids"],
] as const;

export const ORDER_MUTATION_QUERY_KEYS = [
  ...ORDER_SCOPED_QUERY_KEYS,
  ...ORDER_LIST_QUERY_KEYS,
] as const;

interface InvalidatorClient {
  invalidateQueries: (filters: { queryKey: unknown[] }) => unknown;
}

/** Invalidate every order-shaped cache after an order/leg write. */
export function invalidateOrderQueries(queryClient: InvalidatorClient): void {
  for (const key of ORDER_MUTATION_QUERY_KEYS) {
    queryClient.invalidateQueries({ queryKey: key as unknown as unknown[] });
  }
}
